import type { ColorCategory } from '../types';

/**
 * Bilingual one-line intros for each of the 9 deck colors. Single source of
 * truth shared by:
 *   • The per-card color tooltip (`src/ui/color-tooltip.ts`) — shown on
 *     hover/tap of an individual card face.
 *   • The cluster banner above the synthesis (`src/ui/synthesis.ts`) — shown
 *     when ≥2 cards of one color appear in the spread.
 *   • The combined-reading prompt builder (`workers/lib/prompts.ts`) —
 *     injected as cluster context when ≥2 cards of one color appear, so the
 *     synthesis can speak to the cluster's energy without re-listing cards.
 *
 * Voice: deep-sea, observational, slightly archaic in 中文 to match the rest
 * of the app's typography (Cormorant Garamond + Noto Serif SC).
 */
export interface ColorIntro {
  en: string;
  zh: string;
}

export const COLOR_INTROS: Record<ColorCategory, ColorIntro> = {
  red: {
    en: 'Primal fire — anger, passion, intensity. Fierce energy that asks to be channeled, not suppressed.',
    zh: '原初之火——愤怒、激情、强度。炽烈之能，当导之、勿压之。',
  },
  orange: {
    en: 'Joy and playful energy — warm, stimulating. Can invigorate but also overstimulate.',
    zh: '喜悦与玩心之能——暖而激人。可振奋，亦可过劲。',
  },
  yellow: {
    en: 'Duality — trust one view while acknowledging the other. Intuition paired with caution.',
    zh: '双面之色——信一见而不弃其反。直觉与警觉同行。',
  },
  green: {
    en: 'Renewal, gratitude, rest. Let things unfold naturally, grow as they need to.',
    zh: '更新、感恩、休歇。让万物依其所需舒展、生长。',
  },
  blue: {
    en: 'Crossing a threshold — surrender to the flow; trust that this, too, will shift.',
    zh: '越过门槛之色——顺流而行，信此一切终将迁变。',
  },
  purple: {
    en: 'Deepening intuition and connection with the higher self. Stay grounded in the body.',
    zh: '直觉与高我之联结渐深——勿忘安住身体。',
  },
  pink: {
    en: 'Tenderness and love. Approach yourself and others with softness and an open heart.',
    zh: '温柔与爱——待己待人，皆以柔心与敞开之意。',
  },
  white: {
    en: 'Stillness and calm. Moving toward tranquility — what do you need to find it?',
    zh: '静意之色——趋于宁静。得之需何？',
  },
  black: {
    en: 'An invitation to step into a new phase — create space for newness or cultivate courage.',
    zh: '入新章之请——为新事让位，或蓄勇以行。',
  },
};

/**
 * Canonical deck order — used as tiebreak when two cluster colors appear
 * the same number of times, so output is deterministic regardless of the
 * draw order in the spread.
 */
const DECK_ORDER: ColorCategory[] = [
  'red',
  'orange',
  'yellow',
  'green',
  'blue',
  'purple',
  'pink',
  'white',
  'black',
];

/**
 * Returns the categories appearing ≥2 times in the given cards, sorted by
 * count descending then by deck order. Empty array if no clusters.
 */
export function findColorClusters(
  cards: { category: ColorCategory }[],
): { category: ColorCategory; count: number }[] {
  const counts: Partial<Record<ColorCategory, number>> = {};
  for (const c of cards) counts[c.category] = (counts[c.category] ?? 0) + 1;
  return (Object.entries(counts) as [ColorCategory, number][])
    .filter(([, n]) => n >= 2)
    .sort((a, b) => b[1] - a[1] || DECK_ORDER.indexOf(a[0]) - DECK_ORDER.indexOf(b[0]))
    .map(([category, count]) => ({ category, count }));
}
